import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Observable, catchError, debounceTime, defer, distinctUntilChanged, map, of, scan, startWith, switchMap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { CommonService } from '@app/common/common.service';
import { ValuesService } from '@app/common/values/values.service';
import { ApiResponse, Attach, C_value, User } from '@app/shared/_models/common';
import { AttachService } from '@app/shared/attach/attach.service';
import { C_VALUES_RENTAL_PERIOD, C_VALUES_RENTAL_REASONS, jsonheader } from '@app/shared/constants';
import { EventType, ObjectType } from '@app/shared/constants-server';
import { businessDayOf, dayNoonIso } from '@app/shared/time';
import type {
  CreditEntry, Day, DayRange, DueEntry, Instant, LineOutcomeView, LineSummary, RentalDecisionsView, RentalInvoiceView, RentalLedgerEntry,
  RentalLifecycleFull, RentalLineView, SimulationManyView,
} from './rental-billing.model';
import type { RentalDecisionEvent } from './rental-decision-events';

export type DecisionOutcome =
  | { ok: true; event: RentalDecisionEvent; decisionId: number; message: string }
  | { ok: false; event: RentalDecisionEvent; code: number | null; message: string };
export interface DecisionRunResult { done: DecisionOutcome[]; failed: DecisionOutcome | null; notSent: RentalDecisionEvent[]; }
// answers : clé (simulationKey) de la requête à laquelle `view` répond ; absente sans vue.
export interface SimulationState { loading: boolean; view: SimulationManyView | null; error: string | null; answers?: string; }
// at : computed_at de la vue du dossier lue par la saisie ; les mêmes évènements sur une vue rechargée se simulent à nouveau.
export interface SimulationRequest { events: RentalDecisionEvent[] | null; at: Instant | null; }

// Plafond de POST …/simulate_many (SIMULATE_MAX, sales/src/rental/lifecycle.rs) : au-delà, ni simulation ni enregistrement.
export const DECISIONS_MAX = 100;
export function tooManyDecisions(n: number): string | null {
  return n > DECISIONS_MAX ? `Au plus ${DECISIONS_MAX} décisions à la fois (${n} ici) : cochez moins de matériels et enregistrez en plusieurs fois.` : null;
}

// Message du serveur tel quel : les routes /sales/rental_* répondent en texte brut, /event en ApiResponse.
export function serverMessage(e: unknown): string {
  const x = e as any;
  const body = x?.error;
  if (typeof body === 'string' && body.trim()) return body.trim();
  for (const m of [body?.msg, body?.error_msg, body?.message, x?.message]) if (typeof m === 'string' && m.trim()) return m.trim();
  return 'Erreur serveur';
}

// Prescription citée par un fichier d'ordonnance : une par matériel prescrit.
export interface PrescriptionFileRef { line_id: number; id: number; }

// data.rental_prescription : liste de références (une ordonnance pour plusieurs matériels), ou une seule (premiers dépôts du 28/10).
export function prescriptionRefsOf(a: Attach): PrescriptionFileRef[] {
  const v = a.data?.['rental_prescription'];
  return (Array.isArray(v) ? v : v ? [v] : []).map((r: any) => ({ line_id: Number(r?.line_id), id: Number(r?.id) })).filter((r) => r.line_id > 0 && r.id > 0);
}

// Pièce du dossier jointe comme ordonnance : la carte « Ordonnances » la montre, « Documents du dossier » l'écarte.
export function isPrescriptionFile(a: Attach): boolean {
  return a?.data?.['rental_prescription'] != null;
}

@Injectable({ providedIn: 'root' })
export class RentalBillingService {
  private http = inject(HttpClient);
  private values = inject(ValuesService);
  private common = inject(CommonService);
  private injector = inject(Injector);

  private url(orderId: number, tail: string): string {
    return `${environment.server_url}/sales/rental_orders/${orderId}/${tail}`;
  }

  // until = DERNIER jour de la frise demandé, inclus, envoyé à midi heure de Paris (le « + » du décalage part encodé %2B).
  // La vue arrive en instants au décalage de Paris et repart en jours « AAAA-MM-JJ » (lifecycleInDays) : les écrans ne manipulent que des Day.
  // open : sans les matériels repris ni leurs reprises (montants et frise des seules lignes en place).
  getLifecycle(orderId: number, until?: Day | null, open = false): Observable<RentalLifecycleFull> {
    const at = until ? (dayNoonIso(until) ?? until) : null;
    const params: Record<string, string> = { ...(at ? { until: at } : {}), ...(open ? { open: 'true' } : {}) };
    return this.http.get<RentalLifecycleWire>(this.url(orderId, 'lifecycle'), { params }).pipe(map(lifecycleInDays));
  }

  // open : le serveur garde quand même les lignes visées par les décisions.
  simulate(orderId: number, events: RentalDecisionEvent[], open = false): Observable<SimulationManyView> {
    const body = { decisions: events.map((e) => ({ typ: e.typ, data: e.data })) };
    return this.http.post<SimulationManyWire>(this.url(orderId, 'simulate_many'), body, { headers: jsonheader, ...(open ? { params: { open: 'true' } } : {}) }).pipe(map(simulationInDays));
  }

  // Le serveur remplace userid par l'utilisateur du jeton ; un refus revient en success = false ou en erreur HTTP, jamais levé ici.
  postDecision(ev: RentalDecisionEvent): Observable<DecisionOutcome> {
    const envelope = { id: 0, typ: ev.typ, userid: 0, objecttyp: ObjectType.ORDER, objectid: ev.data.order_id, data: ev.data };
    return this.http.post<ApiResponse<{ decision_id?: number }>>(`${environment.server_url}/event`, envelope, { headers: jsonheader }).pipe(
      map((res): DecisionOutcome =>
        res?.success
          ? { ok: true, event: ev, decisionId: Number(res.data?.decision_id ?? 0), message: res.msg ?? '' }
          : { ok: false, event: ev, code: res?.error_code ?? null, message: res?.msg || res?.error_msg || 'Erreur serveur' },
      ),
      catchError((e) => of<DecisionOutcome>({ ok: false, event: ev, code: typeof e?.status === 'number' ? e.status : null, message: serverMessage(e) })),
    );
  }

  // Clôturer (toutes les lignes louées et vendues sans line_ids) ou rouvrir, motif obligatoire ; un refus du serveur dit chaque ligne qui bloque.
  postClosure(orderId: number, op: 'close' | 'reopen', reason?: string): Observable<{ ok: boolean; message: string }> {
    const data = { order_id: orderId, op, ...(op === 'reopen' ? { cancel_reason: reason ?? '' } : {}) };
    const envelope = { id: 0, typ: EventType.RENTAL_CLOSURE, userid: 0, objecttyp: ObjectType.ORDER, objectid: orderId, data };
    return this.http.post<ApiResponse>(`${environment.server_url}/event`, envelope, { headers: jsonheader }).pipe(
      map((res) => ({ ok: !!res?.success, message: (res?.success ? res.msg : res?.msg || res?.error_msg) || (res?.success ? '' : 'Erreur serveur') })),
      catchError((e) => of({ ok: false, message: serverMessage(e) })),
    );
  }

  // En série ; le premier refus arrête la série, les suivants sont rendus non envoyés.
  postDecisions(evs: RentalDecisionEvent[]): Observable<DecisionRunResult> {
    return defer(() => {
      const done: DecisionOutcome[] = [];
      const step = (i: number): Observable<DecisionRunResult> =>
        i >= evs.length
          ? of({ done, failed: null, notSent: [] })
          : this.postDecision(evs[i]).pipe(switchMap((o) => (o.ok ? (done.push(o), step(i + 1)) : of({ done, failed: o, notSent: evs.slice(i + 1) }))));
      return step(0);
    });
  }

  // Fichiers d'ordonnance = pièces du dossier (objecttyp ORDER) marquées data.rental_prescription = [{ line_id, id }…] : aucune structure propre avant les décisions D1-D6.
  getPrescriptionFiles(orderId: number): Observable<Attach[]> {
    return this.http.get<Attach[]>(`${environment.server_url}/docsbyobjectid/${orderId}`).pipe(
      map((docs) => (Array.isArray(docs) ? docs : []).filter((a) => a.objecttyp === ObjectType.ORDER && isPrescriptionFile(a))),
    );
  }

  attachPrescriptionFile(file: File, orderId: number, refs: PrescriptionFileRef[]): Observable<ApiResponse | null> {
    return this.injector.get(AttachService).postAttach$(file, { rental_prescription: refs }, ObjectType.ORDER, orderId);
  }

  getReasons(): Observable<C_value[]> {
    return this.values.getValuesFresh$(C_VALUES_RENTAL_REASONS).pipe(map((v) => (Array.isArray(v) ? (v as C_value[]) : [])));
  }

  getUserNames(): Observable<Map<number, string>> {
    return this.common.getUsers().pipe(
      map((users: User[]) => {
        const names = new Map<number, string>();
        for (const u of users ?? []) if (u?.id != null) names.set(u.id, [u.firstname, u.lastname].filter((s) => !!s?.trim()).join(' ').trim() || `utilisateur ${u.id}`);
        return names;
      }),
    );
  }

  getPeriodNames(): Observable<Map<number, string>> {
    return this.values.getValuesFresh$(C_VALUES_RENTAL_PERIOD).pipe(
      map((v) => {
        const names = new Map<number, string>();
        for (const c of Array.isArray(v) ? (v as C_value[]) : []) if (c?.id != null && c.name) names.set(c.id, c.name);
        return names;
      }),
    );
  }
}

// Vues telles que le serveur les envoie : mêmes clés, mais chaque jour est un instant au décalage de Paris,
// chaque jour (start, end, as_of, *_on, due_*, billed_until…) écrit à midi, qu'il ouvre ou ferme une plage.
// Les vrais instants (computed_at, created.dat, cancelled.dat, realdatdel et return_realdatdel du BL) gardent leur heure et ne sont pas convertis.
export type RentalLifecycleWire = RentalLifecycleFull;
export type SimulationManyWire = SimulationManyView;

// Jour de Paris d'un instant reçu (businessDayOf, shared/time.ts) ; une valeur illisible passe telle quelle.
function dayIn(v: Instant): Day {
  return businessDayOf(v) ?? v;
}

function dayOrNullIn(v: Instant | null | undefined): Day | null {
  return v == null ? null : dayIn(v);
}

function rangeIn<T extends DayRange>(r: T): T {
  return { ...r, start: dayIn(r.start), end: dayIn(r.end) };
}

function rangesIn<T extends DayRange>(list: T[] | null | undefined): T[] {
  return (list ?? []).map(rangeIn);
}

function summaryIn(s: LineSummary): LineSummary {
  return { ...s, next_due_on: dayOrNullIn(s.next_due_on), window_start: dayOrNullIn(s.window_start), window_end: dayOrNullIn(s.window_end), return_end: dayOrNullIn(s.return_end) };
}

// Décisions recopiées de la base : jours convertis, horodatages de saisie et d'annulation gardés tels quels.
export function decisionsInDays(d: RentalDecisionsView): RentalDecisionsView;
export function decisionsInDays(d: RentalDecisionsView | null): RentalDecisionsView | null;
export function decisionsInDays(d: RentalDecisionsView | null): RentalDecisionsView | null {
  if (!d) return null;
  return {
    ...d,
    interruptions: (d.interruptions ?? []).map((i) => ({ ...i, start: dayOrNullIn(i.start), end: dayOrNullIn(i.end) })),
    window: (d.window ?? []).map((w) => ({ ...w, day: dayIn(w.day) })),
    prescriptions: (d.prescriptions ?? []).map((p) => ({ ...p, start: dayIn(p.start), end: dayIn(p.end), prescribed_on: p.prescribed_on == null ? p.prescribed_on : dayIn(p.prescribed_on) })),
  };
}

function ledgerIn(e: RentalLedgerEntry): RentalLedgerEntry {
  return { ...rangeIn(e), invoiced_on: dayOrNullIn(e.invoiced_on), days: rangesIn(e.days), periods: rangesIn(e.periods) };
}

function dueIn(e: DueEntry): DueEntry {
  return { ...rangeIn(e), days: rangesIn(e.days), periods: rangesIn(e.periods) };
}

function creditIn(e: CreditEntry): CreditEntry {
  return { ...rangeIn(e), days: rangesIn(e.days) };
}

function lineIn(l: RentalLineView): RentalLineView {
  return {
    ...l,
    planned_on: dayOrNullIn(l.planned_on), planned_return_on: dayOrNullIn(l.planned_return_on),
    billed_until: dayOrNullIn(l.billed_until), due_start: dayOrNullIn(l.due_start), due_end: dayOrNullIn(l.due_end),
    summary: l.summary ? summaryIn(l.summary) : null, days: rangesIn(l.days), decisions: decisionsInDays(l.decisions),
    ledger: (l.ledger ?? []).map(ledgerIn), due: (l.due ?? []).map(dueIn), credits: (l.credits ?? []).map(creditIn),
  };
}

function invoiceIn(i: RentalInvoiceView): RentalInvoiceView {
  return { ...i, invoiced_on: dayOrNullIn(i.invoiced_on), start: dayOrNullIn(i.start), end: dayOrNullIn(i.end) };
}

export function lifecycleInDays(v: RentalLifecycleWire): RentalLifecycleFull {
  return {
    ...v,
    as_of: dayIn(v.as_of), until: dayOrNullIn(v.until), planned_delivery_on: dayOrNullIn(v.planned_delivery_on),
    lines: (v.lines ?? []).map(lineIn), invoices: (v.invoices ?? []).map(invoiceIn),
  };
}

function outcomeIn(o: LineOutcomeView): LineOutcomeView {
  return {
    ...o, summary: summaryIn(o.summary), billed_until: dayOrNullIn(o.billed_until), days: rangesIn(o.days),
    due: (o.due ?? []).map(dueIn), credits: (o.credits ?? []).map(creditIn),
  };
}

export function simulationInDays(v: SimulationManyWire): SimulationManyView {
  return {
    ...v,
    as_of: dayIn(v.as_of),
    lines: (v.lines ?? []).map((x) => ({ ...x, before: outcomeIn(x.before), after: outcomeIn(x.after), after_decisions: decisionsInDays(x.after_decisions) })),
  };
}

export function simulationKey(events: RentalDecisionEvent[] | null, at: Instant | null): string {
  return JSON.stringify([events, at]);
}

// Simulation 250 ms après la dernière modification ; la requête précédente est abandonnée, la dernière vue reste pendant le calcul ou après un échec.
// Jamais sur un dossier ou une ligne neufs (id 0) : le serveur ne les connaît pas.
export function simulationStream(svc: RentalBillingService, orderId: number, requests$: Observable<SimulationRequest | RentalDecisionEvent[] | null>, open = false): Observable<SimulationState> {
  type Step = { reset: true } | { loading: true } | { view: SimulationManyView; key: string } | { error: string };
  return requests$.pipe(
    map((r) => {
      const req: SimulationRequest = r === null || Array.isArray(r) ? { events: r, at: null } : r;
      const evs = req.events;
      return orderId > 0 && evs && evs.length && evs.length <= DECISIONS_MAX && evs.every((e) => e.data.line_id > 0) ? { evs, key: simulationKey(evs, req.at) } : null;
    }),
    distinctUntilChanged((a, b) => (a?.key ?? null) === (b?.key ?? null)),
    debounceTime(250),
    switchMap((req): Observable<Step> =>
      req === null
        ? of({ reset: true })
        : svc.simulate(orderId, req.evs, open).pipe(
            map((view): Step => ({ view, key: req.key })),
            catchError((e) => of<Step>({ error: serverMessage(e) })),
            startWith<Step>({ loading: true }),
          ),
    ),
    scan((st: SimulationState, s: Step): SimulationState => {
      if ('reset' in s) return { loading: false, view: null, error: null };
      if ('loading' in s) return { ...st, loading: true };
      if ('view' in s) return { loading: false, view: s.view, error: null, answers: s.key };
      return { ...st, loading: false, error: s.error };
    }, { loading: false, view: null, error: null }),
  );
}

// Bilan d'un enregistrement : « Enregistrées », « Refusée (ligne, matériel) : message du serveur », « Non envoyées ».
export function decisionRunText(run: DecisionRunResult, lineLabel: (lineId: number) => string): string {
  const parts: string[] = [];
  if (run.done.length) parts.push('Enregistrées : ' + run.done.map((o) => lineLabel(o.event.data.line_id)).join(', '));
  if (run.failed) parts.push(`Refusée (${lineLabel(run.failed.event.data.line_id)}) : ${run.failed.message}`);
  if (run.notSent.length) parts.push('Non envoyées : ' + run.notSent.map((e) => lineLabel(e.data.line_id)).join(', '));
  return parts.join('\n');
}
